"use client"

import type React from "react"
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react"
import { createClient } from "@/lib/supabase/client"
import type { UserPreferences } from "@/lib/types/user-preferences"
import { useAuthContext } from "@/lib/auth/context/AuthContext"

interface UserPreferencesContextType {
  preferences: UserPreferences | null
  loading: boolean
  updatePreference: (key: keyof UserPreferences, value: any) => Promise<void>
  isAuthenticated: boolean
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined)

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const supabase = createClient()

  // FIX: Use AuthContext instead of creating duplicate subscription
  // This prevents multiple onAuthStateChange subscriptions that cause token refresh storms
  const { user, loading: authLoading } = useAuthContext()

  // Track if we've loaded preferences for this user to avoid duplicate loads
  const loadedForUserId = useRef<string | null>(null)

  const loadPreferences = useCallback(async (userId: string) => {
    // Avoid duplicate loads for the same user
    if (loadedForUserId.current === userId) return

    try {
      // Try to load existing preferences
      const { data: existingPrefs } = await supabase
        .from("user_preferences")
        .select("*")
        .eq("user_id", userId)
        .single()

      if (existingPrefs) {
        setPreferences(existingPrefs)
        loadedForUserId.current = userId
      } else {
        // Create default preferences if none exist
        const defaultPrefs = {
          theme: "system" as const,
          language: "en" as const,
          preferred_branch: null as string | null,
          preferred_purity: null as string | null,
          preferred_order_type: null as string | null,
        }

        const { data: newPrefs } = await supabase
          .from("user_preferences")
          .insert({ ...defaultPrefs, user_id: userId })
          .select()
          .single()

        if (newPrefs) {
          setPreferences(newPrefs as UserPreferences)
          loadedForUserId.current = userId
        }
      }
    } catch (error) {
      console.error("Error loading user preferences:", error)
    }
  }, [supabase])

  // React to user changes from AuthContext
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return

    if (user) {
      setIsAuthenticated(true)
      loadPreferences(user.id).finally(() => setLoading(false))
    } else {
      setIsAuthenticated(false)
      setPreferences(null)
      loadedForUserId.current = null
      setLoading(false)
    }
  }, [user, authLoading, loadPreferences])

  const updatePreference = async (key: keyof UserPreferences, value: any) => {
    if (!preferences || !isAuthenticated || !user) return

    // Optimistic update - update UI immediately
    const updatedPrefs = { ...preferences, [key]: value }
    setPreferences(updatedPrefs)

    try {
      // Update database in background - use user.id from AuthContext
      await supabase
        .from("user_preferences")
        .update({ [key]: value })
        .eq("user_id", user.id)
    } catch (error) {
      console.error("Error updating preference:", error)
      // Revert optimistic update on error
      setPreferences(preferences)
    }
  }

  return (
    <UserPreferencesContext.Provider
      value={{
        preferences,
        loading,
        updatePreference,
        isAuthenticated,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  )
}

export function useGlobalUserPreferences() {
  const context = useContext(UserPreferencesContext)
  if (context === undefined) {
    throw new Error("useGlobalUserPreferences must be used within a UserPreferencesProvider")
  }
  return context
}
